<script lang="ts">
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import * as Select from "#lib/web-ui/ui/select/index.js";
	import * as Dialog from "#lib/web-ui/ui/dialog/index.js";
	import * as Sheet from "#lib/web-ui/ui/sheet/index.js";
	import { Checkbox } from "#lib/web-ui/ui/checkbox/index.js";
	import { Label } from "#lib/web-ui/ui/label/index.js";
	import FunnelIcon from "@lucide/svelte/icons/funnel";
	import RotateCcwIcon from "@lucide/svelte/icons/rotate-cw";
	import { goto } from "$app/navigation";
	import { page } from "$app/state";
	import { sellPropertyTypes, saleListingStatuses } from "./sale-listings-grid.types";

	// Helper function to safely parse numbers
	const parseNumber = (value: string | null): number | null => {
		if (!value) return null;
		const parsed = Number(value);
		return isNaN(parsed) ? null : parsed;
	};

	const params = $derived({
		minPrice: parseNumber(page.url.searchParams.get("minPrice")),
		maxPrice: parseNumber(page.url.searchParams.get("maxPrice")),
		status: page.url.searchParams.get("status"),
		type: page.url.searchParams.get("type"),
		beds: parseNumber(page.url.searchParams.get("beds")),
		baths: parseNumber(page.url.searchParams.get("baths"))
	});

	// Derive checkbox selections from query params - simple flat types
	const selectedPropertyTypes = $derived.by(() => {
		const types: Record<string, boolean> = {};
		if (params.type) {
			const typeParams = params.type.split(",");
			typeParams.forEach((typeParam: string) => {
				const trimmedType = typeParam.trim();
				if (trimmedType) {
					types[trimmedType] = true;
				}
			});
		}
		return types;
	});

	// Toggle a property type
	function togglePropertyType(type: string, checked: boolean) {
		const currentType = params.type || "";
		let typeParams = currentType ? currentType.split(",").map((t) => t.trim()) : [];

		if (checked) {
			// Add type if not already present
			if (!typeParams.includes(type)) {
				typeParams.push(type);
			}
		} else {
			// Remove type
			typeParams = typeParams.filter((t) => t !== type);
		}

		// Update URL immediately
		const url = new URL(page.url.href);
		if (typeParams.length > 0) {
			url.searchParams.set("type", typeParams.join(","));
		} else {
			url.searchParams.delete("type");
		}
		goto(url, { replace: true });
	}

	function clearPropertySelections() {
		const url = new URL(page.url.href);
		url.searchParams.delete("type");
		goto(url, { replace: true });
	}

	function resetFilters() {
		goto(page.url.pathname, { replace: true });
	}

	function countSelectedTypes() {
		return Object.values(selectedPropertyTypes).filter(Boolean).length;
	}

	// Derived values for UI display
	const propertyTypeTrigger = $derived(
		countSelectedTypes() > 0 ? `Property Types (${countSelectedTypes()})` : "Property Type"
	);
	const bedroomsTrigger = $derived(params.beds ? `${params.beds} beds` : "Bedrooms");
	const bathsTrigger = $derived(params.baths ? `${params.baths} baths` : "Baths");
	const statusTrigger = $derived(
		saleListingStatuses.find((status) => status === params.status) || "Status"
	);
</script>

<!-- Mobile Filter Button -->
<div class="mb-4 lg:hidden">
	<Sheet.Root>
		<Sheet.Trigger>
			<div
				class="border-border flex items-center gap-2 rounded border border-solid px-4 py-2"
			>
				<FunnelIcon class="mr-2 h-5 w-5" />
				Filters
			</div>
		</Sheet.Trigger>
		<Sheet.Content side="bottom" class="w-full">
			<Sheet.Header>
				<Sheet.Title>Filter Properties</Sheet.Title>
			</Sheet.Header>
			<div class="mt-4 space-y-4">
				<div class="space-y-2">
					<Label>Price Range</Label>
					<div class="grid grid-cols-2 gap-2">
						<Input
							type="number"
							placeholder="Min Price"
							value={params.minPrice?.toString() || ""}
							step={25000}
							onchange={(e) => {
								const url = new URL(page.url.href);
								const value = (e.target as HTMLInputElement).value;
								if (value) {
									url.searchParams.set("minPrice", value);
								} else {
									url.searchParams.delete("minPrice");
								}
								goto(url, { replace: true });
							}}
						/>

						<Input
							type="number"
							placeholder="Max Price"
							value={params.maxPrice?.toString() || ""}
							step={25000}
							onchange={(e) => {
								const url = new URL(page.url.href);
								const value = (e.target as HTMLInputElement).value;
								if (value) {
									url.searchParams.set("maxPrice", value);
								} else {
									url.searchParams.delete("maxPrice");
								}
								goto(url, { replace: true });
							}}
						/>
					</div>
				</div>
				<div class="space-y-2">
					<Label>Property Type</Label>
					<Sheet.Root>
						<Sheet.Trigger
							class="border-input bg-background text-muted-foreground ring-offset-background focus:ring-ring flex h-10 items-center justify-between rounded border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
						>
							{propertyTypeTrigger}
						</Sheet.Trigger>
						<Sheet.Content side="right" class="w-full">
							<Sheet.Header>
								<Sheet.Title>Select Property Types</Sheet.Title>
								<Sheet.Description>
									Choose property types to filter
								</Sheet.Description>
							</Sheet.Header>
							<div class="mt-6 max-h-[75vh] space-y-3 overflow-y-auto">
								{#each sellPropertyTypes as propType}
									<div class="flex items-center space-x-2">
										<Checkbox
											id={`mobile-${propType}`}
											checked={selectedPropertyTypes[propType]}
											onCheckedChange={(checked) =>
												togglePropertyType(propType, checked)}
										/>
										<Label for={`mobile-${propType}`} class="font-medium">
											{propType}
										</Label>
									</div>
								{/each}
							</div>
							<div class="mt-4 flex justify-between">
								<Button variant="outline" onclick={clearPropertySelections}>
									Clear All
								</Button>
								<Sheet.Close>
									<Dialog.Close>
										<Button class="bg-primary hover:bg-primary/90">Done</Button>
									</Dialog.Close>
								</Sheet.Close>
							</div>
						</Sheet.Content>
					</Sheet.Root>
				</div>

				<!-- Other filters remain the same -->
				<div class="space-y-2">
					<Label>Min Bedrooms</Label>
					<Select.Root
						type="single"
						value={params.beds?.toString()}
						onValueChange={(value) => {
							{
								const url = new URL(page.url.href);
								if (value) {
									url.searchParams.set("beds", value);
								} else {
									url.searchParams.delete("beds");
								}
								goto(url, { replace: true });
							}
						}}
					>
						<Select.Trigger>
							{bedroomsTrigger}
						</Select.Trigger>
						<Select.Content>
							<Select.Group>
								{#each [{ label: "Any", value: 0 }, { label: "1", value: 1 }, { label: "2+", value: 2 }, { label: "3+", value: 3 }, { label: "4+", value: 4 }, { label: "5+", value: 5 }] as bed}
									<Select.Item value={String(bed.value)}>
										{bed.label}
									</Select.Item>
								{/each}
							</Select.Group>
						</Select.Content>
					</Select.Root>
				</div>

				<div class="space-y-2">
					<Label>Min Bathrooms</Label>
					<Select.Root
						type="single"
						value={params.baths?.toString()}
						onValueChange={(value) => {
							const url = new URL(page.url.href);
							if (value) {
								url.searchParams.set("baths", value);
							} else {
								url.searchParams.delete("baths");
							}
							goto(url, { replace: true });
						}}
					>
						<Select.Trigger>
							{bathsTrigger}
						</Select.Trigger>
						<Select.Content>
							<Select.Group>
								{#each [{ label: "Any", value: 0 }, { label: "1+", value: 1 }, { label: "1.5", value: 1.5 }, { label: "2+", value: 2 }, { label: "2.5", value: 2.5 }, { label: "3+", value: 3 }, { label: "3.5", value: 3.5 }] as bath}
									<Select.Item value={String(bath.value)}>
										{bath.label}
									</Select.Item>
								{/each}
							</Select.Group>
						</Select.Content>
					</Select.Root>
				</div>
				<div class="space-y-2">
					<Label>Status</Label>
					<Select.Root
						type="single"
						value={params.status ?? undefined}
						onValueChange={(value) => {
							const url = new URL(page.url.href);
							if (value) {
								url.searchParams.set("status", value);
							} else {
								url.searchParams.delete("status");
							}
							goto(url, { replace: true });
						}}
					>
						<Select.Trigger>
							{statusTrigger}
						</Select.Trigger>
						<Select.Content>
							<Select.Group>
								{#each saleListingStatuses as status}
									<Select.Item value={status}>
										{status}
									</Select.Item>
								{/each}
							</Select.Group>
						</Select.Content>
					</Select.Root>
				</div>
				<div class="mt-4">
					<Button onclick={resetFilters}>
						<RotateCcwIcon class="mr-2 h-4 w-4" />
						Reset Filters
					</Button>
				</div>
			</div>
		</Sheet.Content>
	</Sheet.Root>
</div>

<!-- Desktop Filter Bar -->
<div class="mb-8 hidden flex-wrap gap-4 lg:flex">
	<div class="grid w-64 grid-cols-2 gap-2">
		<Input
			type="number"
			placeholder="Min Price"
			value={params.minPrice?.toString() || ""}
			step={25000}
			onchange={(e) => {
				const url = new URL(page.url.href);
				const value = (e.target as HTMLInputElement).value;
				if (value) {
					url.searchParams.set("minPrice", value);
				} else {
					url.searchParams.delete("minPrice");
				}
				goto(url, { replace: true });
			}}
		/>

		<Input
			type="number"
			placeholder="Max Price"
			value={params.maxPrice?.toString() || ""}
			step={25000}
			onchange={(e) => {
				const url = new URL(page.url.href);
				const value = (e.target as HTMLInputElement).value;
				if (value) {
					url.searchParams.set("maxPrice", value);
				} else {
					url.searchParams.delete("maxPrice");
				}
				goto(url, { replace: true });
			}}
		/>
	</div>

	<!-- Property Type Dialog for Desktop -->
	<Dialog.Root>
		<Dialog.Trigger
			class="border-input bg-background text-muted-foreground ring-offset-background focus:ring-ring flex h-10 w-48 items-center justify-between rounded border px-3 py-2 text-sm hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
		>
			{propertyTypeTrigger}
		</Dialog.Trigger>
		<Dialog.Overlay />
		<Dialog.Content class="max-w-[500px]">
			<Dialog.Header>
				<Dialog.Title>Select Property Types</Dialog.Title>
				<Dialog.Description>Choose property types to filter</Dialog.Description>
			</Dialog.Header>
			<div class="max-h-[60vh] space-y-3 overflow-y-auto px-1">
				{#each sellPropertyTypes as propType}
					<div class="flex items-center space-x-2">
						<Checkbox
							id={`desktop-${propType}`}
							class="border-primary/50 h-5 w-5"
							checked={selectedPropertyTypes[propType]}
							onCheckedChange={(checked) => togglePropertyType(propType, checked)}
						/>
						<Label
							for={`desktop-${propType}`}
							class="text-foreground text-base font-medium"
						>
							{propType}
						</Label>
					</div>
				{/each}
			</div>

			<Dialog.Footer class="mt-6 flex justify-between border-t border-gray-200 pt-4">
				<Button
					variant="outline"
					onclick={clearPropertySelections}
					class="text-muted-foreground"
				>
					Clear All
				</Button>
				<Dialog.Close>
					<Button class="bg-primary hover:bg-primary/90">Done</Button>
				</Dialog.Close>
			</Dialog.Footer>
		</Dialog.Content>
	</Dialog.Root>
	<Select.Root
		type="single"
		value={params.beds?.toString()}
		onValueChange={(value) => {
			const url = new URL(page.url.href);
			if (value) {
				url.searchParams.set("beds", value);
			} else {
				url.searchParams.delete("beds");
			}
			goto(url, { replace: true });
		}}
	>
		<Select.Trigger class="w-40">
			{bedroomsTrigger}
		</Select.Trigger>
		<Select.Content>
			<Select.Group>
				{#each [{ label: "Any", value: 0 }, { label: "1", value: 1 }, { label: "2+", value: 2 }, { label: "3+", value: 3 }, { label: "4+", value: 4 }, { label: "5+", value: 5 }] as bed}
					<Select.Item value={String(bed.value)}>
						{bed.label}
					</Select.Item>
				{/each}
			</Select.Group>
		</Select.Content>
	</Select.Root>
	<Select.Root
		type="single"
		value={params.baths?.toString()}
		onValueChange={(value) => {
			const url = new URL(page.url.href);
			if (value) {
				url.searchParams.set("baths", value);
			} else {
				url.searchParams.delete("baths");
			}
			goto(url, { replace: true });
		}}
	>
		<Select.Trigger class="w-40">
			{bathsTrigger}
		</Select.Trigger>
		<Select.Content>
			<Select.Group>
				{#each [{ label: "Any", value: 0 }, { label: "1+", value: 1 }, { label: "1.5+", value: 1.5 }, { label: "2+", value: 2 }, { label: "2.5+", value: 2.5 }, { label: "3+", value: 3 }, { label: "3.5+", value: 3.5 }] as bath}
					<Select.Item value={String(bath.value)}>
						{bath.label}
					</Select.Item>
				{/each}
			</Select.Group>
		</Select.Content>
	</Select.Root>
	<Select.Root
		type="single"
		value={params.status ?? undefined}
		onValueChange={(value) => {
			const url = new URL(page.url.href);
			if (value) {
				url.searchParams.set("status", value);
			} else {
				url.searchParams.delete("status");
			}
			goto(url, { replace: true });
		}}
	>
		<Select.Trigger class="w-40">
			{statusTrigger}
		</Select.Trigger>
		<Select.Content>
			<Select.Group>
				{#each saleListingStatuses as status}
					<Select.Item value={status}>
						{status}
					</Select.Item>
				{/each}
			</Select.Group>
		</Select.Content>
	</Select.Root>
	<Button variant="outline" onclick={resetFilters} class="flex-none">
		<RotateCcwIcon class="mr-2 h-4 w-4" />
		Reset
	</Button>
</div>
